// Chameleon's FileName validator caps the whole uploaded name, extension
// included (mirrors MAX_FILE_NAME_LENGTH in the marketing-presentation BFF
// route: src/apps/frontend/centro-portal/app/rfps/[id]/marketing-presentation/route.ts).
const SHAREPOINT_FILE_NAME_MAX_LENGTH = 256;

const XLSX_EXTENSION_LENGTH = ".xlsx".length;

// mpx persists version as a Postgres int32, so the suffix reserved here must
// fit the widest value that column can ever hold — int32's max, 2147483647
// (10 digits) — not an arbitrary round number (mirrors
// MAX_VERSION_SUFFIX_LENGTH in the same BFF route).
const MAX_VERSION_SUFFIX_LENGTH = " v2147483647".length;

// What the analyst types here becomes the base name the BFF appends ".xlsx"
// and a possible " v<n>" suffix to, so the limit enforced client-side must
// leave room for both — otherwise a name that passes here still gets
// truncated or mangled server-side.
export const SHAREPOINT_BASE_FILE_NAME_MAX_LENGTH =
  SHAREPOINT_FILE_NAME_MAX_LENGTH -
  XLSX_EXTENSION_LENGTH -
  MAX_VERSION_SUFFIX_LENGTH;

const SHAREPOINT_ILLEGAL_CHARACTERS = /["#%&*:<>?/\\{|}~]/;
const SHAREPOINT_ILLEGAL_CHARACTERS_DISPLAY = '~ " # % & * : < > ? / \\ { | }';

// Mirrors .NET's char.IsControl: C0 (below space), DEL, and the C1 range.
const isControlCharacter = (char: string): boolean => {
  const code = char.charCodeAt(0);
  return code < 0x20 || (code >= 0x7f && code <= 0x9f);
};

const containsControlCharacter = (fileName: string): boolean =>
  [...fileName].some(isControlCharacter);

interface SharePointFileNameValidationRule {
  message: string;
  isViolated: (fileName: string) => boolean;
}

const SHAREPOINT_FILE_NAME_VALIDATION_RULES: readonly SharePointFileNameValidationRule[] =
  [
    {
      message: "File name is required.",
      isViolated: (fileName) => fileName.trim() === "",
    },
    {
      message: `File name must be ${SHAREPOINT_BASE_FILE_NAME_MAX_LENGTH} characters or fewer.`,
      isViolated: (fileName) =>
        fileName.length > SHAREPOINT_BASE_FILE_NAME_MAX_LENGTH,
    },
    {
      message: `File name can't contain any of these characters: ${SHAREPOINT_ILLEGAL_CHARACTERS_DISPLAY}`,
      isViolated: (fileName) => SHAREPOINT_ILLEGAL_CHARACTERS.test(fileName),
    },
    {
      message: "File name can't start or end with a space.",
      isViolated: (fileName) =>
        fileName.startsWith(" ") || fileName.endsWith(" "),
    },
    {
      message:
        "File name contains invalid hidden characters. Try retyping the name.",
      isViolated: containsControlCharacter,
    },
    {
      message: "File name can't end with a period.",
      isViolated: (fileName) => fileName.endsWith("."),
    },
    {
      message: "File name can't contain two periods in a row.",
      isViolated: (fileName) => /\.{2,}/.test(fileName),
    },
  ];

/**
 * Returns every SharePoint file name rule `fileName` violates, in a stable
 * order, so the caller can display all reasons an export upload would fail.
 * An empty array means the name is valid.
 */
export const validateSharePointFileName = (fileName: string): string[] =>
  SHAREPOINT_FILE_NAME_VALIDATION_RULES.filter((rule) =>
    rule.isViolated(fileName),
  ).map((rule) => rule.message);

export const isValidSharePointFileName = (fileName: string): boolean =>
  validateSharePointFileName(fileName).length === 0;
